import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Row } from 'react-table';

import { AuthContext } from 'auth';
import { Host } from 'hooks';

import { AVAILABLE_MOUSE_BUTTONS } from './constants';
import {
  EHostModalType,
  EMouseButton,
  IHostCoordinates,
  IHostModalsData,
} from './types';

export function useHostModals(
  disableMultiSelect?: boolean,
  selectedItems?: Set<string>,
  hosts?: Array<Host>,
  setSelectedItems?: Dispatch<SetStateAction<Set<string>>>
): IHostModalsData {
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [type, setType] = useState<EHostModalType>(EHostModalType.NONE);
  const [selectedHostsIds, setSelectedHostsIds] = useState<Array<number>>([]);
  const [bookingIdForEdit, setBookingIdForEdit] = useState<number | null>(null);
  const [penalty, setPenalty] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [hostCoords, setHostCoords] = useState<IHostCoordinates | null>(null);
  const [sidebarHostId, setSidebarHostId] = useState<number | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (type === EHostModalType.NONE) {
      setBookingIdForEdit(null);
      setPenalty(null);
      setGroupId(null);
      setHostCoords(null);
    }
  }, [type]);

  useEffect(() => {
    if (selectedItems) {
      const idsArray = Array.from(selectedItems)
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      setSelectedHostsIds(idsArray);
    }
  }, [selectedItems]);

  const onHostClick = useCallback(
    (
      event: MouseEvent,
      hostId: number,
      rowIndex?: number,
      flatRows?: Row<Host>[]
    ): void => {
      if (!AVAILABLE_MOUSE_BUTTONS.includes(event.button)) {
        return;
      }

      if (selectedItems?.size !== 0 && selectedItems !== undefined) return;

      if (event.button === EMouseButton.RIGHT) {
        setLastSelectedIndex(null);
        setSelectedHostsIds((prevState) => {
          if (prevState.includes(hostId)) {
            return prevState;
          }
          return [hostId];
        });
        return;
      }

      if (event.shiftKey && flatRows && rowIndex !== undefined) {
        if (lastSelectedIndex === null) {
          setLastSelectedIndex(rowIndex);
        }

        const safeLastSelectedIndex = lastSelectedIndex ?? rowIndex;

        const startIndex = Math.min(safeLastSelectedIndex, rowIndex);
        const endIndex = Math.max(safeLastSelectedIndex, rowIndex);

        flatRows.forEach((row) => (row.isSelected = false));

        const rowsToSelect = flatRows.slice(startIndex, endIndex + 1);
        rowsToSelect.forEach((row) => (row.isSelected = true));

        setSelectedHostsIds(rowsToSelect.map((row) => row.original.id));
        return;
      }

      if (event.ctrlKey && !disableMultiSelect && rowIndex !== undefined) {
        setLastSelectedIndex(rowIndex);
        setSelectedHostsIds((prevState) => {
          if (prevState.includes(hostId)) {
            return prevState.filter((id) => id !== hostId);
          }
          return [...prevState, hostId];
        });
        return;
      }

      if (!event.ctrlKey) {
        if (rowIndex !== undefined) {
          setLastSelectedIndex(rowIndex);
          setSelectedHostsIds([hostId]);
          setSidebarHostId(hostId);
        } else {
          setSelectedHostsIds([hostId]);
          setSidebarHostId(hostId);
        }
      }
    },
    [disableMultiSelect, lastSelectedIndex]
  );

  const unselectHosts = useCallback(() => {
    setSelectedHostsIds([]);
    setSelectedItems && setSelectedItems(new Set());
  }, []);

  const openShop = useCallback(
    (hostId: number): void => {
      if (!permissions?.has('payment_create')) return;

      setSelectedHostsIds(() => [hostId]);
      setType(EHostModalType.SHOP);
    },
    [permissions]
  );

  return {
    type,
    setType,
    bookingIdForEdit,
    setBookingIdForEdit,
    penalty,
    setPenalty,
    selectedHostsIds,
    setSelectedHostsIds,
    groupId,
    setGroupId,
    onHostClick,
    unselectHosts,
    openShop,
    sidebarHostId,
    setSidebarHostId,
    hostCoords,
    setHostCoords,
    hosts,
  };
}
